/**
 * Mobile header drawer — tabs + in-panel submenus (search/tabs always visible).
 * Disables Prestige sidebar open/reveal animations for instant menu load.
 */
(function () {
  const DRAWER_ID = 'sidebar-menu';
  const TAB_ACTIVE = 'is-active';
  const SUBMENU_OPEN_CLASS = 'header-sidebar--submenu-open';
  const MOBILE_MQ = '(max-width: 999px)';
  const INSTANT_CLASS = 'header-sidebar--instant';

  const instantAnimation = { finished: Promise.resolve() };

  function forceSidebarVisible(drawer) {
    const mainPanel = drawer.querySelector('.header-sidebar__main-panel');
    if (mainPanel) {
      mainPanel.style.opacity = '1';
      mainPanel.style.transform = 'translateX(0)';
    }

    drawer.querySelectorAll('.header-sidebar__main-panel .header-sidebar__linklist li').forEach((item) => {
      item.style.opacity = '1';
      item.style.transform = 'translateY(0)';
    });

    const footer = drawer.querySelector('.header-sidebar__footer');
    if (footer) {
      footer.style.opacity = '1';
      footer.style.transform = 'translateY(0)';
    }
  }

  function patchInstantSidebarAnimations() {
    const HeaderSidebar = customElements.get('header-sidebar');
    const CollapsiblePanel = customElements.get('header-sidebar-collapsible-panel');

    if (HeaderSidebar && !HeaderSidebar.prototype.__guttelusInstantPatched) {
      HeaderSidebar.prototype.__guttelusInstantPatched = true;

      HeaderSidebar.prototype.createEnterAnimationControls = function () {
        const content = this.getShadowPartByName('content');
        const overlay = this.getShadowPartByName('overlay');
        const openFrom = this.getAttribute('open-from') || 'right';

        if (content) {
          content.style.marginInlineStart = openFrom === 'right' ? 'auto' : '0';
          content.style.transform = 'translateX(0)';
        }

        if (overlay) overlay.style.opacity = '1';

        forceSidebarVisible(this);
        return instantAnimation;
      };

      HeaderSidebar.prototype.createLeaveAnimationControls = function () {
        return instantAnimation;
      };

      HeaderSidebar.prototype.revealItems = function () {
        forceSidebarVisible(this);
        return instantAnimation;
      };
    }

    if (CollapsiblePanel && !CollapsiblePanel.prototype.__guttelusInstantPatched) {
      CollapsiblePanel.prototype.__guttelusInstantPatched = true;

      CollapsiblePanel.prototype.createEnterAnimationControls = function () {
        this.style.opacity = '1';
        this.style.transform = 'translateX(0)';
        return instantAnimation;
      };

      CollapsiblePanel.prototype.createLeaveAnimationControls = function () {
        return instantAnimation;
      };
    }
  }

  customElements.whenDefined('header-sidebar').then(patchInstantSidebarAnimations);
  customElements.whenDefined('header-sidebar-collapsible-panel').then(patchInstantSidebarAnimations);

  function getDrawer() {
    return document.getElementById(DRAWER_ID);
  }

  function isMobile() {
    return window.matchMedia(MOBILE_MQ).matches;
  }

  function setActiveTab(drawer, tabName) {
    drawer.querySelectorAll('[data-mobile-tab]').forEach((tab) => {
      const isActive = tab.getAttribute('data-mobile-tab') === tabName;
      tab.classList.toggle(TAB_ACTIVE, isActive);
      tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
    });

    drawer.querySelectorAll('[data-mobile-panel]').forEach((panel) => {
      const isActive = panel.getAttribute('data-mobile-panel') === tabName;
      panel.hidden = !isActive;
    });
  }

  function getMobileStack(drawer) {
    return drawer.querySelector('[data-mobile-submenu-stack]');
  }

  function openMobileSubmenu(drawer, panelId) {
    const stack = getMobileStack(drawer);
    const tabScroller = drawer.querySelector('.header-sidebar__tab-scroller');
    if (!stack || !panelId) return;

    const panel = drawer.querySelector(`[data-mobile-submenu-stack] #${CSS.escape(panelId)}`);
    if (!panel) return;

    drawer.classList.add(SUBMENU_OPEN_CLASS);
    if (tabScroller) tabScroller.hidden = true;
    stack.hidden = false;

    stack.querySelectorAll('.header-sidebar__sub-panel').forEach((subPanel) => {
      subPanel.hidden = subPanel !== panel;
    });

    drawer.querySelectorAll('.header-sidebar__main-panel [aria-controls]').forEach((btn) => {
      btn.setAttribute('aria-expanded', btn.getAttribute('aria-controls') === panelId ? 'true' : 'false');
    });

    const tabName = panel.getAttribute('data-mobile-submenu-tab');
    if (tabName) setActiveTab(drawer, tabName);
  }

  function closeMobileSubmenu(drawer) {
    const stack = getMobileStack(drawer);
    const tabScroller = drawer.querySelector('.header-sidebar__tab-scroller');
    const collapsible = drawer.querySelector('header-sidebar-collapsible-panel');

    drawer.classList.remove(SUBMENU_OPEN_CLASS);

    if (stack) stack.hidden = true;
    if (tabScroller) tabScroller.hidden = false;

    if (stack) {
      stack.querySelectorAll('.header-sidebar__sub-panel').forEach((subPanel) => {
        subPanel.hidden = true;
      });
    }

    drawer.querySelectorAll('.header-sidebar__main-panel [aria-controls]').forEach((btn) => {
      btn.setAttribute('aria-expanded', 'false');
    });

    if (collapsible && isMobile()) {
      collapsible.removeAttribute('aria-activedescendant');
    }
  }

  function resetDrawer(drawer) {
    setActiveTab(drawer, 'categories');
    closeMobileSubmenu(drawer);
  }

  function initMobileSubmenu(drawer) {
    drawer.querySelectorAll('.header-sidebar__main-panel [aria-controls]').forEach((button) => {
      button.addEventListener(
        'click',
        (event) => {
          if (!isMobile()) return;

          event.preventDefault();
          event.stopImmediatePropagation();

          const panelId = button.getAttribute('aria-controls');
          openMobileSubmenu(drawer, panelId);
        },
        true
      );
    });

    drawer.querySelectorAll('[data-mobile-submenu-close]').forEach((button) => {
      button.addEventListener('click', (event) => {
        if (!isMobile()) return;

        event.preventDefault();
        event.stopImmediatePropagation();
        closeMobileSubmenu(drawer);
      });
    });
  }

  function initDrawer(drawer) {
    if (drawer.dataset.mobileTabsInit === 'true') return;
    drawer.dataset.mobileTabsInit = 'true';

    if (!drawer.classList.contains(INSTANT_CLASS)) {
      drawer.classList.add(INSTANT_CLASS);
    }

    drawer.addEventListener('dialog:before-show', () => {
      forceSidebarVisible(drawer);
    });

    drawer.addEventListener('dialog:after-show', () => {
      forceSidebarVisible(drawer);
    });

    drawer.querySelectorAll('[data-mobile-tab]').forEach((tab) => {
      tab.addEventListener('click', () => {
        closeMobileSubmenu(drawer);
        setActiveTab(drawer, tab.getAttribute('data-mobile-tab'));
      });
    });

    drawer.addEventListener('dialog:after-hide', () => resetDrawer(drawer));

    initMobileSubmenu(drawer);
  }

  function init() {
    patchInstantSidebarAnimations();

    const drawer = getDrawer();
    if (drawer) initDrawer(drawer);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => {
    patchInstantSidebarAnimations();

    const drawer = event.target.querySelector(`#${DRAWER_ID}`) || getDrawer();
    if (drawer) {
      drawer.dataset.mobileTabsInit = 'false';
      initDrawer(drawer);
    }
  });
})();
